import { useState, useEffect } from 'react';
import apiClient from '../../lib/api';
import { resolveImageUrl } from '../../lib/utils';

interface GalleryImage {
  id: string;
  title: string;
  description?: string;
  image_url: string;
  category: string;
  order_index: number;
  is_active: number;
}

interface Testimonial {
  id: string;
  name: string;
  location?: string;
  rating: number;
  comment: string;
  image_url?: string;
  is_featured?: number;
  is_active: number;
}

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageSettings, setPageSettings] = useState({
    hero_title: 'Our Gallery',
    hero_subtitle: 'Discover our work and luxurious salon environment',
    hero_image_url: ''
  });

  const [categories, setCategories] = useState<any[]>([
    { id: 'all', name: 'All', icon: 'ri-image-line' }
  ]);

  useEffect(() => {
    loadGalleryData();
  }, []);

  const loadGalleryData = async () => {
    try {
      setError(null);
      const [galleryData, testimonialsData, settings, galleryCategories] = await Promise.all([
        apiClient.getGalleryItems(),
        apiClient.getTestimonials(),
        apiClient.getGalleryPageSettings(),
        apiClient.getGalleryCategories()
      ]);

      let filteredGallery = galleryData.filter((item: GalleryImage) => item.is_active);
      setGalleryImages(filteredGallery);
      setTestimonials(testimonialsData.filter((testimonial: Testimonial) => testimonial.is_active && testimonial.is_featured));
      setPageSettings(settings);

      // Create categories array with 'All' first, then dynamic categories from gallery items
      let dynamicCategories = galleryCategories.map((cat: any) => {
        const categoryIcons: { [key: string]: string } = {
          'Interior': 'ri-home-4-line',
          'Services': 'ri-scissors-line',
          'Hair Cuts': 'ri-scissors-line',
          'Hair Color': 'ri-palette-line',
          'Facial': 'ri-user-smile-line',
          'Styling': 'ri-brush-line',
          'Before & After': 'ri-contrast-line'
        };
        return {
          id: cat.name || cat,
          name: cat.name || cat,
          icon: cat.icon || categoryIcons[cat.name || cat] || 'ri-image-line'
        };
      });

      if (dynamicCategories.length === 0) {
        dynamicCategories = [];
      }

      setCategories([
        { id: 'all', name: 'All', icon: 'ri-image-line' },
        ...dynamicCategories
      ]);
    } catch (error) {
      console.error('Failed to load gallery data:', error);
      setError('Failed to load gallery data. Please check if the backend is running.');
      // Fallback to empty arrays if API fails
      setGalleryImages([]);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };


  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter(image => image.category === activeCategory);

  const whatsappBooking = () => {
    const message = "Hi! I saw your gallery and would love to book an appointment. Please let me know your availability.";
    window.open(`https://wa.me/917484984048?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Gallery Unavailable</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadGalleryData}
            className="bg-primary-400 text-white px-6 py-2 rounded-full hover:bg-primary-500 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=luxury%20mens%20salon%20gallery%20showcase%2C%20premium%20grooming%20establishment%20interior%2C%20professional%20barber%20shop%20with%20elegant%20styling%20stations%2C%20sophisticated%20salon%20atmosphere&width=1920&height=800&seq=galleryHero&orientation=landscape')`
        }}
      >
        <div className="container text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 text-shadow-luxury animate-fadeInUp">
            {pageSettings.hero_title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            {pageSettings.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="container">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 whitespace-nowrap cursor-pointer ${activeCategory === category.id
                  ? 'bg-primary-400 text-black'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-glow transition-all duration-500 animate-fadeInUp ${image.category === 'Interior' ? 'md:col-span-2' : ''
                    }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img
                    src={resolveImageUrl(image.image_url)}
                    alt={image.title}
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${image.category === 'Interior' ? 'h-64' : 'h-80'
                      }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-lg font-bold mb-1">{image.title}</h3>
                      <p className="text-sm text-gray-300">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-3xl">
              <i className="ri-image-line text-6xl text-gray-300 mb-4 block"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Images Found</h3>
              <p className="text-gray-600">The gallery will be updated soon with our latest work.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 luxury-gradient">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Our Achievements
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Excellence in Numbers
            </h3>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center animate-fadeInUp">
              <div className="text-4xl md:text-5xl font-bold text-primary-400 mb-2">5000+</div>
              <div className="text-white font-semibold">Happy Clients</div>
            </div>
            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              <div className="text-4xl md:text-5xl font-bold text-primary-400 mb-2">10+</div>
              <div className="text-white font-semibold">Years Experience</div>
            </div>
            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <div className="text-4xl md:text-5xl font-bold text-primary-400 mb-2">27</div>
              <div className="text-white font-semibold">Premium Services</div>
            </div>
            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
              <div className="text-4xl md:text-5xl font-bold text-primary-400 mb-2">3</div>
              <div className="text-white font-semibold">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Client Reviews
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.length > 0 ? (
              testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-2xl p-6 shadow-lg animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-400"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "{testimonial.comment}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <img
                      src={resolveImageUrl(testimonial.image_url) || `https://readdy.ai/api/search-image?query=professional%20customer%20portrait&width=60&height=60&seq=testimonial${testimonial.id}&orientation=squarish`}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      {testimonial.location && (
                        <p className="text-gray-600 text-sm">{testimonial.location}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-10">
                <p className="text-gray-600 italic">No featured testimonials yet.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gold-gradient">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Ready to Join Our Success Stories?
            </h2>
            <p className="text-xl text-gray-800 mb-8 leading-relaxed">
              Experience the luxury and quality that our clients rave about. Book your appointment today and see the difference for yourself.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={whatsappBooking}
                className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer"
              >
                Book Now
              </button>
              <a
                href="tel:+917484984048"
                className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer inline-block"
              >
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;